import { cn } from "@/lib/utils";
import { Shield } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

interface MobileNavProps {
	open: boolean;
	onClose: () => void;
	isAdmin: boolean;
}

export function MobileNav({ open, onClose, isAdmin }: MobileNavProps) {
	const { t } = useTranslation();
	const location = useLocation();

	// Lock body scroll when open
	useEffect(() => {
		if (open) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "";
		}
		return () => {
			document.body.style.overflow = "";
		};
	}, [open]);

	if (!open) return null;

	const navLinks = [
		{ href: "/", label: t("nav.dashboard") },
		{ href: "/courses", label: t("nav.courses") },
		{ href: "/profile", label: t("nav.profile") },
	];

	return (
		<div
			className="fixed inset-0 z-overlay bg-bg-base/95 backdrop-blur-md md:hidden"
			aria-label="Navigation menu"
		>
			<nav className="flex h-full flex-col items-center justify-center gap-8">
				{navLinks.map((link) => {
					const active = location.pathname === link.href;
					return (
						<Link
							key={link.href}
							to={link.href}
							onClick={onClose}
							aria-current={active ? "page" : undefined}
							className={cn(
								"min-h-[44px] px-4 py-2 text-2xl font-semibold transition-colors",
								active ? "text-accent" : "text-secondary hover:text-primary",
							)}
						>
							{link.label}
						</Link>
					);
				})}
				{isAdmin && (
					<Link
						to="/admin"
						onClick={onClose}
						aria-current={location.pathname.startsWith("/admin") ? "page" : undefined}
						className={cn(
							"flex min-h-[44px] items-center gap-2 px-4 py-2 text-2xl font-semibold transition-colors",
							location.pathname.startsWith("/admin")
								? "text-accent"
								: "text-secondary hover:text-primary",
						)}
					>
						<Shield className="h-6 w-6" aria-hidden="true" />
						{t("nav.admin")}
					</Link>
				)}
			</nav>
		</div>
	);
}
